import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Typography,
  CardContent,
  CardHeader,
  Card,
  Button,
  Alert,
  CardActions,
  List,
  ListItemText,
  ListItem,
  Stepper,
  useMediaQuery,
  Step,
  StepButton,
  Container,
  AlertTitle,
  ListItemAvatar
} from '@mui/material'
import { useTheme } from '@mui/system'
import { useTranslation } from 'react-i18next'
import LoadingState from '../components/LoadingState'
import { getDevice } from '../services/deviceService'
import { AssetInput, Company, Device, Project, RuuviTag, RuuviTagInput } from '../types'
import { getProject } from '../services/projectService'
import { RuuviTagForm } from '../components/RuuviTagForm'
import { formatMacAddress } from '../utils/string'
import { createOrUpdateRuuviTag } from '../services/ruuviTagService'
import { uploadAssetPhoto } from '../services/assetService'
import { NavLink, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { createWarningNotification } from '../redux/slices/notifications'
import PageHeader from '../components/PageHeader'
import SelectProjectStep from '../components/installation/SelectProjectStep'
import ScanQrCodeOrEnterDeviceCodeStep from '../components/installation/ScanQrCodeOrEnterDeviceCodeStep'
import RuuviTagScanner from '../components/installation/RuuviTagScanner'
import { getCompany } from '../services/companyService'
import RuuviTagDetailsList from '../components/RuuviTagDetailsList'
// state persisted to URL query params (useSearchParams)

interface AddDetailsStepProps {
  values: RuuviTagInput
  onBack: () => void
  onExit: () => void
  onChange: (values: RuuviTagInput) => void
  onSubmit: (values: RuuviTagInput) => Promise<void>
}

function AddDetailsStep({ values, onBack, onExit, onChange, onSubmit }: AddDetailsStepProps) {
  /**
   * The translation function.
   */
  const [t] = useTranslation('common')
  const { t: tInstallation } = useTranslation('installation')

  /**
   * Handle the submit event.
   */
  const handleSubmit = async (values: RuuviTagInput) => {
    await onSubmit(values)
  }

  /**
   * Handle the cancel event.
   */
  const handleBack = () => {
    onBack()
  }

  return (
    <Card>
      <CardHeader
        title={t('addDetails')}
        action={
          <Button color="error" onClick={onExit}>
            {tInstallation('exitInstallation')}
          </Button>
        }
      />
      <CardContent>
        <RuuviTagForm
          values={values}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          onChange={onChange}
          cancelButtonLabel={tInstallation('back')}
        />
      </CardContent>
    </Card>
  )
}

interface InstallationCompletedStepProps {
  ruuviTag: RuuviTag
  onInstallNew: () => void
}

function InstallationCompletedStep({ ruuviTag, onInstallNew }: InstallationCompletedStepProps) {
  /**
   * The translation function.
   */
  const [t] = useTranslation('common')

  /**
   * Handle the install new action.
   */
  const handleInstallNew = () => {
    onInstallNew()
  }

  return (
    <Card>
      <CardHeader title={t('installationCompleted')} />
      <CardContent>
        <Alert variant="outlined" color="success" sx={{ mb: 6 }}>
          <Typography>{t('tagInstalled')}</Typography>
        </Alert>

        <List>
          {ruuviTag?.asset?.name && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.name} secondary={t('name')} />
            </ListItem>
          )}

          <ListItem>
            <ListItemText primary={formatMacAddress(ruuviTag.id)} secondary={t('macAddress')} />
          </ListItem>

          <ListItem>
            <ListItemText primary={ruuviTag.deviceName} secondary={t('hostDevice')} />
          </ListItem>

          {ruuviTag?.asset?.company && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.company.name} secondary={t('company')} />
            </ListItem>
          )}

          {ruuviTag?.asset?.project && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.project.name} secondary={t('project')} />
            </ListItem>
          )}

          {ruuviTag?.asset?.level && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.level.name} secondary={t('level')} />
            </ListItem>
          )}

          {ruuviTag?.asset?.lift && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.lift.name} secondary={t('lift')} />
            </ListItem>
          )}
        </List>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}
      >
        <Button variant="outlined" onClick={handleInstallNew}>
          {t('installNew')}
        </Button>

        <Button
          variant="contained"
          color="success"
          component={NavLink}
          to={`/ruuvi-tags/${ruuviTag.id}`}
        >
          {t('completed')}
        </Button>
      </CardActions>
    </Card>
  )
}

function InstallationInstructionAlert() {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  return (
    <Alert severity="info">
      <AlertTitle sx={{ m: 0 }}>{t('pleaseFollowTheInstructionsBelow')}</AlertTitle>

      <List>
        <ListItem sx={{ py: 0 }}>
          <ListItemAvatar sx={{ minWidth: 16 }}>1.</ListItemAvatar>
          <ListItemText primary={t('makeSureThatTheTagIsTurnedOn')} />
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <ListItemAvatar sx={{ minWidth: 16 }}>2.</ListItemAvatar>
          <ListItemText primary={t('takeTheTagAsCloseAsPossibleToThePowerSupply')} />
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <ListItemAvatar sx={{ minWidth: 16 }}>3.</ListItemAvatar>
          <ListItemText primary={t('flipTheTagUpsideDownAndKeepItThereUntilItHasBeenIdentified')} />
        </ListItem>
      </List>
    </Alert>
  )
}

enum Steps {
  SELECT_PROJECT = 0,
  SCAN_QR_CODE_OR_ENTER_DEVICE_CODE = 1,
  ENABLE_RUUVITAG = 2,
  ADD_DETAILS = 3,
  INSTALLATION_COMPLETED = 4
}

function clampStep(value: number): Steps {
  if (value <= Steps.SELECT_PROJECT) {
    return Steps.SELECT_PROJECT
  }

  if (value >= Steps.INSTALLATION_COMPLETED) {
    return Steps.INSTALLATION_COMPLETED
  }

  return value as Steps
}

function createEmptyRuuviTagInput(): RuuviTagInput {
  return {
    id: '',
    device: null,
    asset: {
      name: '',
      description: '',
      project: null,
      company: null,
      level: null,
      lift: null
    }
  }
}

function mergeRuuviTagInput(base: RuuviTagInput, overrides?: RuuviTagInput): RuuviTagInput {
  const baseAsset = ensureAssetInput(base.asset)

  const overrideAsset: Partial<AssetInput> = overrides?.asset ?? {}

  return {
    id: overrides?.id ?? base.id ?? '',
    device: overrides?.device ?? base.device ?? null,
    asset: {
      name: overrideAsset.name ?? baseAsset.name,
      description: overrideAsset.description ?? baseAsset.description,
      project: overrideAsset.project ?? baseAsset.project,
      company: overrideAsset.company ?? baseAsset.company,
      level: overrideAsset.level ?? baseAsset.level,
      lift: overrideAsset.lift ?? baseAsset.lift,
      photo: overrideAsset.photo ?? baseAsset.photo,
      attachedAt: overrideAsset.attachedAt ?? baseAsset.attachedAt ?? null,
      detachedAt: overrideAsset.detachedAt ?? baseAsset.detachedAt ?? null
    }
  }
}

function ensureAssetInput(asset?: AssetInput): AssetInput {
  return {
    name: asset?.name ?? '',
    description: asset?.description ?? '',
    project: asset?.project ?? null,
    company: asset?.company ?? null,
    level: asset?.level ?? null,
    lift: asset?.lift ?? null,
    photo: asset?.photo ?? null,
    attachedAt: asset?.attachedAt ?? null,
    detachedAt: asset?.detachedAt ?? null
  }
}

export default function InstallRuuviTag() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The current step.
   */
  const [searchParams, setSearchParams] = useSearchParams()

  const [step, setStep] = useState<Steps>(() => {
    const s = searchParams.get('step')
    return s ? (clampStep(parseInt(s)) as Steps) : Steps.SELECT_PROJECT
  })

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The ID of the selected company.
   * Initialize from URL search params so we don't re-read them on every change
   * (avoids a update -> write -> read cycle).
   */
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(() => {
    const c = searchParams.get('company')
    return c ? parseInt(c) : null
  })

  /**
   * The selected company.
   */
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)

  /**
   * The ID of the selected project.
   * Initialize from URL search params so the initial UI reflects the URL.
   */
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(() => {
    const p = searchParams.get('project')
    return p ? parseInt(p) : undefined
  })

  /**
   * The selected project.
   */
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)

  /**
   * The name of the selected device.
   * Initialize from URL search params so the initial UI reflects the URL.
   */
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | undefined>(() => {
    return searchParams.get('device') ?? undefined
  })

  /**
   * The selected device.
   */
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)

  /**
   * The ruuvi tag being installed.
   */
  const [ruuviTag, setRuuviTag] = useState<RuuviTag | null>(null)

  /**
   * The form input.
   */
  const [input, setInput] = useState<RuuviTagInput>(() => createEmptyRuuviTagInput())
  const formSnapshotRef = useRef<string>('')
  const skipReplaceRef = useRef(false)
  // Keep internal state in sync with external navigation (Back/Forward).
  // We initialize from search params on mount, but we also need to react when
  // the user navigates via the browser history which updates search params.
  useEffect(() => {
    const s = searchParams.get('step')
    const stepFromUrl = s ? (clampStep(parseInt(s)) as Steps) : Steps.SELECT_PROJECT

    if (stepFromUrl !== step) {
      setStep(stepFromUrl)
    }

    const p = searchParams.get('project')
    const projectFromUrl = p ? parseInt(p) : undefined
    if (projectFromUrl !== selectedProjectId) {
      setSelectedProjectId(projectFromUrl)
    }

    const c = searchParams.get('company')
    const companyFromUrl = c ? parseInt(c) : null
    if (companyFromUrl !== selectedCompanyId) {
      setSelectedCompanyId(companyFromUrl)
    }

    const deviceFromUrl = searchParams.get('device') ?? undefined
    if (deviceFromUrl !== selectedDeviceName) {
      setSelectedDeviceName(deviceFromUrl)
    }
  }, [searchParams])
  // Initialize state from search params and keep it in sync with the URL.

  // Sync non-step state to URL (replace current history entry so these don't create navigation entries).
  // Preserve the current `step` param so it doesn't get removed when other fields update.
  useEffect(() => {
    // If we just pushed a history entry (skipReplaceRef), don't run the replace
    // which would otherwise overwrite the pushed entry and break Back navigation.
    if (skipReplaceRef.current) {
      // reset the guard and skip this replace
      skipReplaceRef.current = false
      return
    }

    const params = new URLSearchParams()

    // Keep the step in the query so Back/Refresh restores the correct wizard step.
    params.set('step', String(step))

    if (selectedProjectId !== undefined) params.set('project', String(selectedProjectId))
    if (selectedCompanyId !== null && selectedCompanyId !== undefined)
      params.set('company', String(selectedCompanyId))
    if (selectedDeviceName) params.set('device', selectedDeviceName)
    if (ruuviTag?.id) params.set('ruuvitag', ruuviTag.id)

    setSearchParams(params, { replace: true })
  }, [
    selectedProjectId,
    selectedCompanyId,
    selectedDeviceName,
    ruuviTag?.id,
    step,
    setSearchParams
  ])

  // Push a history entry when a device is first selected while on the scan step.
  // This ensures the browser Back button returns from the device summary to the
  // enter-code view (both are within the same wizard step).
  const _prevDeviceNameRef = useRef<string | undefined>(undefined)
  const _initialMountRef = useRef(true)

  useEffect(() => {
    // Skip during initial mount/hydration — do not push when value comes from URL.
    if (_initialMountRef.current) {
      _initialMountRef.current = false
      _prevDeviceNameRef.current = selectedDeviceName
      return
    }

    if (
      step === Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE &&
      selectedDeviceName &&
      !_prevDeviceNameRef.current
    ) {
      const params = new URLSearchParams()
      params.set('step', String(step))
      if (selectedProjectId !== undefined) params.set('project', String(selectedProjectId))
      if (selectedCompanyId !== null && selectedCompanyId !== undefined)
        params.set('company', String(selectedCompanyId))
      params.set('device', selectedDeviceName)
      if (ruuviTag?.id) params.set('ruuvitag', ruuviTag.id)

      // Push a new history entry so Back navigates to the previous scan state.
      // Mark skipReplaceRef so the replace-effect does not immediately overwrite this push.
      skipReplaceRef.current = true
      setSearchParams(params)

      // reset guard on next tick as a safety fallback (in case other effects don't run)
      setTimeout(() => {
        skipReplaceRef.current = false
      }, 0)
    }

    _prevDeviceNameRef.current = selectedDeviceName
  }, [
    selectedDeviceName,
    step,
    selectedProjectId,
    selectedCompanyId,
    ruuviTag?.id,
    setSearchParams
  ])

  /**
   * Handle next event.
   */
  const handleNext = () => {
    setStep((currentStep) => {
      const next = clampStep(currentStep + 1)

      return next
    })
  }

  const handleBack = () => {
    if (step <= Steps.SELECT_PROJECT) {
      return
    }

    setStep((currentStep) => {
      const prev = clampStep(currentStep - 1)

      return prev
    })
  }

  const handleFormChange = (values: RuuviTagInput) => {
    const serializableValues = {
      ...values,
      asset: values.asset
        ? {
            ...values.asset,
            photo: undefined
          }
        : undefined
    }

    const serialized = JSON.stringify(serializableValues)

    if (serialized === formSnapshotRef.current) {
      return
    }

    formSnapshotRef.current = serialized

    setInput((previous) => mergeRuuviTagInput(previous, values))
  }

  /**
   * Handle the cancel event.
   */
  const handleCancel = () => {
    setStep(() => Steps.SELECT_PROJECT)
    setSelectedProjectId(undefined)
    setSelectedDeviceName(undefined)
    setSelectedCompanyId(null)
    setSelectedCompany(null)
    setSelectedProject(null)
    setSelectedDevice(null)
    setRuuviTag(null)
    setInput(createEmptyRuuviTagInput())
    formSnapshotRef.current = ''
    setSearchParams({}, { replace: true })
  }

  /**
   * Handle the submit event.
   */
  const handleSubmit = async (values: RuuviTagInput) => {
    try {
      // Create or update the ruuvi tag.
      const ruuviTag = await createOrUpdateRuuviTag(values)

      // Now, if photo was provided, we should upload it.
      if (ruuviTag.assetId !== null && values?.asset?.photo) {
        const asset = await uploadAssetPhoto(ruuviTag.assetId, values.asset.photo)

        // Omit nested device and ruuvi tag from asset.
        delete asset.device
        delete asset.ruuviTag

        ruuviTag.asset = asset
      }

      setRuuviTag(ruuviTag)
      setStep(Steps.INSTALLATION_COMPLETED)
    } catch (_error) {
      dispatch(createWarningNotification(t('tagInstallationFailed')))
    }
  }

  async function loadSelectedProject(id: number, controller?: AbortController) {
    setSelectedProject(await getProject(id, { signal: controller?.signal }))
  }

  async function loadSelectedCompany(id: number, controller?: AbortController) {
    setSelectedCompany(await getCompany(id, { signal: controller?.signal }))
  }

  async function loadSelectedDevice(name: string, controller?: AbortController) {
    setSelectedDevice(await getDevice(name, { signal: controller?.signal }))
  }

  /**
   * Update the input when the selected project changes.
   */
  useEffect(() => {
    if (selectedProjectId) {
      const controller = new AbortController()

      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            project: selectedProjectId
          }
        }
      })

      loadSelectedProject(selectedProjectId, controller)

      return () => controller.abort()
    } else {
      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            project: null,
            company: null,
            level: null,
            lift: null
          }
        }
      })

      setSelectedProject(null)
    }
  }, [selectedProjectId])

  useEffect(() => {
    if (selectedProject?.companyId) {
      const controller = new AbortController()

      setSelectedCompanyId(selectedProject.companyId)

      return () => controller.abort()
    } else {
      setSelectedCompanyId(null)
    }
  }, [selectedProject])

  useEffect(() => {
    if (selectedCompanyId) {
      const controller = new AbortController()

      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            company: selectedCompanyId
          }
        }
      })

      loadSelectedCompany(selectedCompanyId, controller)

      return () => controller.abort()
    } else {
      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            company: null
          }
        }
      })

      setSelectedCompany(null)
    }
  }, [selectedCompanyId])

  /**
   * Update the input when the selected device changes.
   */
  useEffect(() => {
    if (selectedDeviceName) {
      const controller = new AbortController()

      setInput((previous) => ({
        ...previous,
        device: selectedDeviceName
      }))

      loadSelectedDevice(selectedDeviceName)

      return () => controller.abort()
    } else {
      setInput((previous) => ({
        ...previous,
        device: null
      }))
      setSelectedDevice(null)
    }
  }, [selectedDeviceName])

  /**
   * Update the input when the paired ruuvi tag changes.
   */
  useEffect(() => {
    if (ruuviTag) {
      setInput((previous) => ({
        ...previous,
        id: ruuviTag.id,
        device: ruuviTag.deviceName
      }))
    } else {
      setInput((previous) => ({
        ...previous,
        id: '',
        device: null
      }))
    }
  }, [ruuviTag])

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * Indicates if the screen is mobile.
   */
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <React.Fragment>
      <PageHeader title={t('installRuuviTag')} />

      <Stepper
        nonLinear
        activeStep={step}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{ mt: 6, mb: 12 }}
      >
        <Step
          key={Steps.SELECT_PROJECT}
          completed={step > Steps.SELECT_PROJECT}
          color={step > Steps.SELECT_PROJECT ? 'success' : 'primary'}
        >
          <StepButton color="inherit">{t('selectProject')}</StepButton>
        </Step>
        <Step
          key={Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE}
          completed={step > Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE}
        >
          <StepButton color="inherit">{t('scanQrCodeOrEnterDeviceCode')}</StepButton>
        </Step>
        <Step key={Steps.ENABLE_RUUVITAG} completed={step > Steps.ENABLE_RUUVITAG}>
          <StepButton color="inherit">{t('enableRuuviTag')}</StepButton>
        </Step>
        <Step key={Steps.ADD_DETAILS} completed={step > Steps.ADD_DETAILS}>
          <StepButton color="inherit">{t('enterRuuviTagDetails')}</StepButton>
        </Step>
        <Step key={Steps.INSTALLATION_COMPLETED} completed={step === Steps.INSTALLATION_COMPLETED}>
          <StepButton color="inherit">{t('installationCompleted')}</StepButton>
        </Step>
      </Stepper>

      <Container maxWidth="md">
        {step === Steps.SELECT_PROJECT ? (
          <SelectProjectStep
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            onCancel={handleCancel}
            onNext={handleNext}
          />
        ) : step === Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE ? (
          <ScanQrCodeOrEnterDeviceCodeStep
            initialDeviceName={selectedDeviceName}
            onChange={setSelectedDeviceName}
            onCancel={handleCancel}
            onBack={handleBack}
            withDetachAlert={false}
            onlineRequired
            onNext={handleNext}
          />
        ) : step === Steps.ENABLE_RUUVITAG ? (
          <>
            {!selectedProject || !selectedDevice ? (
              <LoadingState />
            ) : (
              <RuuviTagSetupStep
                device={selectedDevice}
                ruuviTag={ruuviTag}
                onChange={setRuuviTag}
                onBack={handleBack}
                onExit={handleCancel}
                onNext={handleNext}
              />
            )}
          </>
        ) : step === Steps.ADD_DETAILS ? (
          <AddDetailsStep
            values={input}
            onBack={handleBack}
            onExit={handleCancel}
            onChange={handleFormChange}
            onSubmit={handleSubmit}
          />
        ) : step === Steps.INSTALLATION_COMPLETED ? (
          <>
            {ruuviTag ? (
              <InstallationCompletedStep ruuviTag={ruuviTag} onInstallNew={handleCancel} />
            ) : (
              <Card>
                <CardHeader title={t('installationCompleted')} />
                <CardContent>
                  <Alert severity="error" variant="outlined">
                    {t('somethingWentWrong')}
                  </Alert>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Button onClick={handleCancel}>{t('cancel')}</Button>
                </CardActions>
              </Card>
            )}
          </>
        ) : null}
      </Container>
    </React.Fragment>
  )
}

interface RuuviTagSetupStepProps {
  device: Device
  ruuviTag: RuuviTag | null
  onChange: (ruuviTag: RuuviTag | null) => void
  onBack: () => void
  onExit: () => void
  onNext: () => void
}

function RuuviTagSetupStep({
  device,
  ruuviTag,
  onChange,
  onBack,
  onExit,
  onNext
}: RuuviTagSetupStepProps) {
  const { t } = useTranslation('installation')

  function handleBack() {
    onBack()
  }

  function handleExit() {
    onChange(null)
    onExit()
  }

  return (
    <Card>
      <CardHeader title={t('enableRuuviTag')} />
      <CardContent sx={{ pt: 0 }}>
        {ruuviTag ? (
          <RuuviTagDetailsList ruuviTag={ruuviTag} showDevice={false} />
        ) : (
          <RuuviTagScanner device={device} onChange={onChange} onCancel={handleExit} />
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="text" color="inherit" onClick={handleBack}>
          {t('back')}
        </Button>
        <Button variant="text" color="error" onClick={handleExit}>
          {t('exitInstallation')}
        </Button>
        <Button variant="contained" color="primary" disabled={ruuviTag === null} onClick={onNext}>
          {t('next')}
        </Button>
      </CardActions>
    </Card>
  )
}
